import React, { useMemo, useRef, useState, useLayoutEffect, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { Navigation, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/scrollbar'

import { FacebookPreview } from 'components/Recommendations/EventCampaigns/Steps/Setup/Summary/Previews/V2/FacebookPreview'

import integrationsApi from 'api/integrations'
import ReelsPreview from 'components/Recommendations/EventCampaigns/Steps/Setup/Summary/Previews/V2/ReelsPreview'
import InstagramPreview from 'components/Recommendations/EventCampaigns/Steps/Setup/Summary/Previews/V2/InstagramPreview'
import FacebookRightColumnPreview from 'components/Recommendations/EventCampaigns/Steps/Setup/Summary/Previews/V2/FacebookRightColumnPreview'
import LoadingSpinner from 'components/Shared/LoadingSpinner'

const AdPreviewCard = ({ ad, onLoaded }) => {
  const renderPreview = () => {
    switch (ad.type) {
      case 'facebook_web':
        return (
          <FacebookPreview
            data={ad.data}
            onLoaded={onLoaded}
            placement={ad.placement}
            label={ad.label}
          />
        )
      case 'facebook_mobile':
        return (
          <FacebookPreview
            data={ad.data}
            mobileView
            onLoaded={onLoaded}
            placement={ad.placement}
            label={ad.label}
          />
        )
      case 'instagram':
        return (
          <InstagramPreview
            data={ad.data}
            onLoaded={onLoaded}
            placement={ad.placement}
            label={ad.label}
          />
        )
      case 'reel':
        return (
          <ReelsPreview
            data={ad.data}
            onLoaded={onLoaded}
            placement={ad.placement}
            label={ad.label}
          />
        )
      case 'facebook_right_column':
        return (
          <FacebookRightColumnPreview data={ad.data} placement={ad.placement} label={ad.label} />
        )
      default:
        return null
    }
  }

  return <div className="feature-preview-card">{renderPreview()}</div>
}

AdPreviewCard.propTypes = {
  ad: PropTypes.object.isRequired,
  onLoaded: PropTypes.func,
}

function generatePlacementsForAd(ad) {
  const placements = []
  const { media } = ad
  const rAspectRatio = media ? media.height / media.width  : 1
  const isReel = rAspectRatio > 1.4
  const isTinyMedia = media? media.width < 320 && media.height < 200 : false
  const nameParts = ad.name.split('_')
  const label = nameParts.length > 1
    ? nameParts.slice(-2).join('_')
    : ad.name
  // Facebook Feed Desktop
  if (!isReel) {
    placements.push({
      type: 'facebook_web',
      data: { ...ad, ...ad._metadata },
      placement: 'Facebook feed desktop',
      label: label,
    })
    // Facebook Feed Mobile
    placements.push({
      type: 'facebook_mobile',
      data: { ...ad, ...ad._metadata },
      placement: 'Facebook feed mobile',
      label: label,
    })
    // Instagram Feed
    placements.push({
      type: 'instagram',
      data: { ...ad, ...ad._metadata },
      placement: 'Instagram feed',
      label: label,
    })
    // Facebook Right Column
    if (isTinyMedia) {
      placements.push({
        type: 'facebook_right_column',
        data: { ...ad, ...ad._metadata },
        placement: 'Facebook right column',
        label: label,
      })
    }
  } else {
    placements.push({
      type: 'reel',
      data: { ...ad, ...ad._metadata },
      placement: 'Instagram reels',
      label: label,
    })
  }

  return placements
}

// Accept eventId as a prop for clarity
const BestPerformingAds = ({ eventId }) => {
  const { t } = useTranslation()
  const swiperRef = useRef(null)
  const prevRef = useRef(null)
  const nextRef = useRef(null)
  const containerRef = useRef(null)
  const CARD_WIDTH_BASE = 305
  const CARD_WIDTH = CARD_WIDTH_BASE + 55 // (55 extra padding)
  const [slidesPerView, setSlidesPerView] = useState(3)
  const [ads, setAds] = useState([])
  // metadata is now only attached to each ad, not used as state
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let isMounted = true
    if (!eventId) return
    setLoading(true)
    integrationsApi.fetchTopPerformingAds(eventId).then((res) => {
      if (!isMounted) return
      const data = res.data || res
      const adsWithMetadata = (data.ads || []).map((ad) => ({ ...ad, _metadata: data.metadata }))
      setAds(adsWithMetadata)
      setLoading(false)
    })
    return () => {
      isMounted = false
    }
  }, [eventId])

  // Flatten all placements for all ads, in order
  const previews = ads.flatMap(generatePlacementsForAd)
  const previewsLength = previews.length

  // Dynamically calculate slidesPerView based on container width
  useLayoutEffect(() => {
    let debounceTimer = null
    function updateSlidesPerView() {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth
        const maxSlides = Math.max(1, Math.floor(containerWidth / CARD_WIDTH))
        setSlidesPerView(Math.min(maxSlides, previewsLength || 3))
      }
    }
    function debouncedUpdate() {
      if (debounceTimer) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(updateSlidesPerView, 100)
    }
    // Use requestAnimationFrame to ensure DOM is painted
    const raf = requestAnimationFrame(updateSlidesPerView)
    window.addEventListener('resize', debouncedUpdate)
    return () => {
      window.removeEventListener('resize', debouncedUpdate)
      cancelAnimationFrame(raf)
      if (debounceTimer) clearTimeout(debounceTimer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewsLength])

  const swiperWidth = useMemo(() => {
    return slidesPerView * 305 + (slidesPerView - 1) * 16
  }, [slidesPerView])

  useEffect(() => {
    if (
      swiperRef.current &&
      prevRef.current &&
      nextRef.current &&
      swiperRef.current.params.navigation
    ) {
      swiperRef.current.params.navigation.prevEl = prevRef.current
      swiperRef.current.params.navigation.nextEl = nextRef.current
      swiperRef.current.navigation.destroy()
      swiperRef.current.navigation.init()
      swiperRef.current.navigation.update()
    }
  }, [previewsLength, slidesPerView, prevRef.current, nextRef.current])

  if (!loading && !previews.length) {
    return null
  }

  return (
    <div className="feature-performing-ads event-series-stats card">
      <div
        className="d-flex justify-content-between w-100 card-header"
        style={{ background: 'none' }}
      >
        <h2 className="card-title-default d-inline-block">
          {t('Dashboard.bestPerformingAds.title')}
        </h2>
      </div>
      <div className="card-body">
        <div className="feature-carousel-container" ref={containerRef}>
          {loading && (
            <div className="text-center w-100">
              <LoadingSpinner showText title={t('common.loading')}></LoadingSpinner>
            </div>
          )}
          {!loading && (
            <>
              <button
                ref={prevRef}
                className="feature-carousel-button feature-carousel-button-prev"
                onClick={() => swiperRef.current.slidePrev()}
              >
                <i class="fa-solid fa-chevron-left"></i>
              </button>
              <div
                style={{
                  width: swiperWidth,
                  margin: '0 auto',
                }}
              >
                <Swiper
                  onSwiper={(swiper) => (swiperRef.current = swiper)}
                  modules={[Navigation, Pagination]}
                  spaceBetween={16}
                  slidesPerView={slidesPerView}
                  className="feature-swiper"
                  style={{
                    '--swiper-navigation-size': '18px',
                    '--swiper-navigation-color': '#007bff',
                  }}
                >
                  {previews.map((ad, index) => (
                    <SwiperSlide
                      key={`${ad.id}-${index}`}
                      className="feature-swiper-slide"
                      style={{ width: 305 }}
                    >
                      <AdPreviewCard ad={ad} />
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
              <button
                ref={nextRef}
                className="feature-carousel-button feature-carousel-button-next"
                onClick={() => swiperRef.current.slideNext()}
              >
                <i class="fa-solid fa-chevron-right"></i>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

BestPerformingAds.propTypes = {
  eventId: PropTypes.string.isRequired,
}

export default BestPerformingAds
